import React, { useState, useRef, useEffect } from 'react';
import { Snippet, useSnippets } from '../hooks/useSnippets';
import { useLanguage } from '../hooks/useLanguage';

interface SnippetsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectSnippet: (snippet: Snippet) => void;
  currentContent?: string;
  onSaveCurrentAsSnippet?: (name: string, description?: string) => void;
  autoOpenSaveDialog?: boolean;
  onSaveDialogOpened?: () => void;
}

export const SnippetsModal: React.FC<SnippetsModalProps> = ({
  isOpen,
  onClose,
  onSelectSnippet,
  currentContent,
  onSaveCurrentAsSnippet,
  autoOpenSaveDialog = false,
  onSaveDialogOpened
}) => {
  const { isPortuguese } = useLanguage();
  
  const {
    snippets,
    isLoaded,
    addSnippet,
    updateSnippet,
    deleteSnippet,
    getPredefinedSnippets,
    getCustomSnippets,
    searchSnippets,
    reloadSnippets
  } = useSnippets(isPortuguese ? 'pt' : 'en');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'predefined' | 'custom'>('all');
  const [isCreating, setIsCreating] = useState(false);
  const [editingSnippet, setEditingSnippet] = useState<Snippet | null>(null);
  const [newSnippetName, setNewSnippetName] = useState('');
  const [newSnippetDescription, setNewSnippetDescription] = useState('');
  const [newSnippetContent, setNewSnippetContent] = useState('');
  const [showSaveCurrentDialog, setShowSaveCurrentDialog] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter snippets based on active tab and search query
  const filteredSnippets = () => {
    let baseSnippets = snippets;
    
    if (activeTab === 'predefined') {
      baseSnippets = getPredefinedSnippets();
    } else if (activeTab === 'custom') {
      baseSnippets = getCustomSnippets();
    }
    
    if (searchQuery.trim()) {
      return searchSnippets(searchQuery).filter(snippet => {
        if (activeTab === 'predefined') return snippet.category === 'predefined';
        if (activeTab === 'custom') return snippet.category === 'custom';
        return true;
      });
    }
    
    return baseSnippets;
  };

  // Focus search input when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Auto-open save dialog when requested
  useEffect(() => {
    if (isOpen && autoOpenSaveDialog && currentContent && onSaveCurrentAsSnippet) {
      setTimeout(() => {
        startSavingCurrent();
        if (onSaveDialogOpened) {
          onSaveDialogOpened();
        }
      }, 200); // Small delay to ensure modal is fully rendered
    }
  }, [isOpen, autoOpenSaveDialog, currentContent, onSaveCurrentAsSnippet, onSaveDialogOpened]);

  // Handle escape key and outside clicks
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isCreating || editingSnippet || showSaveCurrentDialog) {
          setIsCreating(false);
          setEditingSnippet(null);
          setShowSaveCurrentDialog(false);
        } else {
          onClose();
        }
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        if (!isCreating && !editingSnippet && !showSaveCurrentDialog) {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isCreating, editingSnippet, showSaveCurrentDialog, onClose]);

  const handleCreateSnippet = () => {
    if (!newSnippetName.trim() || !newSnippetContent.trim()) return;
    
    try {
      addSnippet(newSnippetName.trim(), newSnippetContent.trim(), newSnippetDescription.trim() || undefined);
      
      setIsCreating(false);
      setNewSnippetName('');
      setNewSnippetDescription('');
      setNewSnippetContent('');
      
      // Force reload and switch to custom tab
      setTimeout(() => {
        reloadSnippets();
        setActiveTab('custom');
      }, 100);
    } catch (error) {
      console.error('Error creating snippet:', error);
    }
  };

  const handleUpdateSnippet = () => {
    if (!editingSnippet || !newSnippetName.trim() || !newSnippetContent.trim()) return;
    
    try {
      updateSnippet(editingSnippet.id, {
        name: newSnippetName.trim(),
        content: newSnippetContent.trim(),
        description: newSnippetDescription.trim() || undefined
      });
      
      setEditingSnippet(null);
      setNewSnippetName('');
      setNewSnippetDescription('');
      setNewSnippetContent('');
      
      // Force reload
      setTimeout(() => {
        reloadSnippets();
      }, 100);
    } catch (error) {
      console.error('Error updating snippet:', error);
    }
  };

  const handleSaveCurrentContent = () => {
    if (!currentContent || !onSaveCurrentAsSnippet) return;
    if (!newSnippetName.trim()) return;
    
    try {
      // Use currentContent directly instead of relying on the textarea
      const result = onSaveCurrentAsSnippet(newSnippetName.trim(), newSnippetDescription.trim() || undefined);
      
      // Reset form state
      setShowSaveCurrentDialog(false);
      setNewSnippetName('');
      setNewSnippetDescription('');
      setNewSnippetContent('');
      
      // Force reload of snippets and switch to custom tab
      setTimeout(() => {
        reloadSnippets();
        setActiveTab('custom');
      }, 150);
      
    } catch (error) {
      console.error('Error saving current content:', error);
    }
  };

  const startEditing = (snippet: Snippet) => {
    setEditingSnippet(snippet);
    setNewSnippetName(snippet.name);
    setNewSnippetDescription(snippet.description || '');
    setNewSnippetContent(snippet.content);
    setIsCreating(false);
    setShowSaveCurrentDialog(false);
  };

  const startCreating = () => {
    setIsCreating(true);
    setEditingSnippet(null);
    setShowSaveCurrentDialog(false);
    setNewSnippetName('');
    setNewSnippetDescription('');
    setNewSnippetContent('');
  };

  const startSavingCurrent = () => {
    setShowSaveCurrentDialog(true);
    setIsCreating(false);
    setEditingSnippet(null);
    setNewSnippetName('');
    setNewSnippetDescription('');
    // Auto-fill with current content
    setNewSnippetContent(currentContent || '');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div
        ref={modalRef}
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {isPortuguese ? 'Biblioteca de Snippets' : 'Snippets Library'}
          </h2>
          <div className="flex items-center space-x-2">
            {currentContent && onSaveCurrentAsSnippet && (
              <button
                onClick={startSavingCurrent}
                className="px-3 py-1.5 text-sm bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors duration-200"
                title={isPortuguese ? 'Salvar prompt atual como snippet (Ctrl+Alt+L)' : 'Save current prompt as snippet (Ctrl+Alt+L)'}
              >
                <svg className="w-4 h-4 mr-1.5 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                {isPortuguese ? 'Salvar Atual' : 'Save Current'}
              </button>
            )}
            <button
              onClick={startCreating}
              className="px-3 py-1.5 text-sm bg-green-50 text-green-600 hover:bg-green-100 rounded-lg transition-colors duration-200"
            >
              <svg className="w-4 h-4 mr-1.5 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {isPortuguese ? 'Criar Novo' : 'Create New'}
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Search and Tabs */}
        <div className="px-6 py-4 border-b border-gray-200 space-y-4">
          {/* Search */}
          <div className="relative">
            <input
              ref={searchInputRef}
              type="text"
              placeholder={isPortuguese ? 'Buscar snippets...' : 'Search snippets...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1">
            {[
              { key: 'all', label: isPortuguese ? 'Todos' : 'All' },
              { key: 'predefined', label: isPortuguese ? 'Pré-definidos' : 'Predefined' },
              { key: 'custom', label: isPortuguese ? 'Personalizados' : 'Custom' }
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key as typeof activeTab)}
                className={`px-4 py-2 text-sm rounded-lg transition-colors duration-200 ${
                  activeTab === key
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Snippets List */}
          <div className="w-1/2 border-r border-gray-200 overflow-y-auto">
            {!isLoaded ? (
              <div className="p-6 text-center text-gray-500">
                {isPortuguese ? 'Carregando snippets...' : 'Loading snippets...'}
              </div>
            ) : filteredSnippets().length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                {searchQuery.trim() 
                  ? (isPortuguese ? 'Nenhum snippet encontrado' : 'No snippets found')
                  : (isPortuguese ? 'Nenhum snippet disponível' : 'No snippets available')
                }
              </div>
            ) : (
              <div className="p-4 space-y-2">
                {filteredSnippets().map((snippet) => (
                  <div
                    key={snippet.id}
                    className="group p-3 border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-sm transition-all duration-200 cursor-pointer"
                    onClick={() => onSelectSnippet(snippet)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 truncate group-hover:text-blue-600">
                          {snippet.name}
                        </h3>
                        {snippet.description && (
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                            {snippet.description}
                          </p>
                        )}
                        <div className="flex items-center mt-2 space-x-2 text-xs text-gray-400">
                          <span className={`px-2 py-0.5 rounded-full ${
                            snippet.category === 'predefined' 
                              ? 'bg-blue-100 text-blue-600' 
                              : 'bg-green-100 text-green-600'
                          }`}>
                            {snippet.category === 'predefined' 
                              ? (isPortuguese ? 'Pré-definido' : 'Predefined')
                              : (isPortuguese ? 'Personalizado' : 'Custom')
                            }
                          </span>
                          <span>{new Date(snippet.updatedAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                      {snippet.category === 'custom' && (
                        <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              startEditing(snippet);
                            }}
                            className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors duration-200"
                            title={isPortuguese ? 'Editar' : 'Edit'}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(isPortuguese ? 'Tem certeza que deseja excluir este snippet?' : 'Are you sure you want to delete this snippet?')) {
                                deleteSnippet(snippet.id);
                                setTimeout(() => {
                                  reloadSnippets();
                                }, 100);
                              }
                            }}
                            className="p-1.5 text-gray-400 hover:text-red-600 transition-colors duration-200"
                            title={isPortuguese ? 'Excluir' : 'Delete'}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Form Panel */}
          <div className="w-1/2 flex flex-col">
            {(isCreating || editingSnippet || showSaveCurrentDialog) ? (
              <div className="p-6 flex-1 flex flex-col">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {showSaveCurrentDialog 
                    ? (isPortuguese ? 'Salvar Prompt Atual' : 'Save Current Prompt')
                    : editingSnippet 
                      ? (isPortuguese ? 'Editar Snippet' : 'Edit Snippet')
                      : (isPortuguese ? 'Criar Novo Snippet' : 'Create New Snippet')
                  }
                </h3>
                
                <div className="space-y-4 flex-1 flex flex-col">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {isPortuguese ? 'Nome' : 'Name'}
                    </label>
                    <input
                      type="text"
                      value={newSnippetName}
                      onChange={(e) => setNewSnippetName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={isPortuguese ? 'Nome do snippet' : 'Snippet name'}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {isPortuguese ? 'Descrição (opcional)' : 'Description (optional)'}
                    </label>
                    <input
                      type="text"
                      value={newSnippetDescription}
                      onChange={(e) => setNewSnippetDescription(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={isPortuguese ? 'Breve descrição do snippet' : 'Brief description of the snippet'}
                    />
                  </div>

                  {showSaveCurrentDialog ? (
                    <div className="flex-1 flex flex-col">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {isPortuguese ? 'Conteúdo (atual do editor)' : 'Content (from editor)'}
                      </label>
                      <textarea
                        value={currentContent || ''}
                        readOnly
                        className="flex-1 w-full px-3 py-2 border border-gray-300 bg-gray-50 rounded-lg resize-none text-sm"
                        placeholder={isPortuguese ? 'Conteúdo do editor...' : 'Content from editor...'}
                      />
                    </div>
                  ) : (
                    <div className="flex-1 flex flex-col">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {isPortuguese ? 'Conteúdo' : 'Content'}
                      </label>
                      <textarea
                        value={newSnippetContent}
                        onChange={(e) => setNewSnippetContent(e.target.value)}
                        className="flex-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        placeholder={isPortuguese ? 'Conteúdo do snippet...' : 'Snippet content...'}
                      />
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setIsCreating(false);
                      setEditingSnippet(null);
                      setShowSaveCurrentDialog(false);
                      setNewSnippetName('');
                      setNewSnippetDescription('');
                      setNewSnippetContent('');
                    }}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors duration-200"
                  >
                    {isPortuguese ? 'Cancelar' : 'Cancel'}
                  </button>
                  <button
                    onClick={showSaveCurrentDialog ? handleSaveCurrentContent : editingSnippet ? handleUpdateSnippet : handleCreateSnippet}
                    disabled={!newSnippetName.trim() || (!showSaveCurrentDialog && !newSnippetContent.trim())}
                    className="px-4 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition-colors duration-200"
                  >
                    {showSaveCurrentDialog
                      ? (isPortuguese ? 'Salvar' : 'Save')
                      : editingSnippet
                        ? (isPortuguese ? 'Atualizar' : 'Update')
                        : (isPortuguese ? 'Criar' : 'Create')
                    }
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-sm">
                  {isPortuguese 
                    ? 'Selecione um snippet para usar ou crie um novo'
                    : 'Select a snippet to use or create a new one'
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};